import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import * as SearchExportActions from './search-export.actions';
import { SearchExportService } from '../../services/search-export.service';
import { PopupService } from '../../services/popup.service';

@Injectable()
export class SearchExportEffects {
  constructor(
    private actions$: Actions,
    private searchExportService: SearchExportService,
    private popupService: PopupService
  ) {}

  // ============================================
  // Start Export Effects
  // ============================================

  startSearchExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchExportActions.startSearchExport),
      switchMap(({ search, tags, archived, workspace_id, sort_by, sort_dir, date_range, search_report_ids }) =>
        this.searchExportService.startExport({ search, tags, archived, workspace_id, sort_by, sort_dir, date_range, search_report_ids }).pipe(
          map((result) =>
            SearchExportActions.startSearchExportSuccess({ result })
          ),
          catchError((error) =>
            of(SearchExportActions.startSearchExportFailure({ error }))
          )
        )
      )
    )
  );

  startSearchExportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SearchExportActions.startSearchExportSuccess),
        tap(({ result }) => {
          this.popupService.notify(
            result.message || 'Export started successfully!',
            'Success',
            'success'
          );
        })
      ),
    { dispatch: false }
  );

  startSearchExportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SearchExportActions.startSearchExportFailure),
        tap(({ error }) => {
          const message = error?.error?.message || error?.message || 'Failed to start export';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );

  // ============================================
  // Load Exports Effects
  // ============================================

  loadSearchExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchExportActions.loadSearchExports),
      switchMap(({ status, page, perPage }) =>
        this.searchExportService.listExports({
          status,
          page,
          per_page: perPage
        }).pipe(
          map((response) =>
            SearchExportActions.loadSearchExportsSuccess({
              exports: response.data,
              pagination: response.pagination
            })
          ),
          catchError((error) =>
            of(SearchExportActions.loadSearchExportsFailure({ error }))
          )
        )
      )
    )
  );

  loadSearchExportsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SearchExportActions.loadSearchExportsFailure),
        tap(({ error }) => {
          const message = error?.error?.message || error?.message || 'Failed to load exports';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );

  // ============================================
  // Download Export Effects
  // ============================================

  downloadSearchExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchExportActions.downloadSearchExport),
      switchMap(({ exportId, fileName }) =>
        this.searchExportService.downloadExport(exportId).pipe(
          map(({ blob, fileName: responseFileName }) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || responseFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return SearchExportActions.downloadSearchExportSuccess({ exportId });
          }),
          catchError((error) =>
            of(SearchExportActions.downloadSearchExportFailure({ exportId, error }))
          )
        )
      )
    )
  );

  downloadSearchExportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SearchExportActions.downloadSearchExportSuccess),
        tap(() => {
          this.popupService.notify('Export downloaded successfully!', 'Success', 'success');
        })
      ),
    { dispatch: false }
  );

  downloadSearchExportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SearchExportActions.downloadSearchExportFailure),
        tap(({ error }) => {
          const message = error?.error?.message || error?.message || 'Failed to download export';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );
}
